import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useDeviceProfile } from '../hooks/useDeviceProfile';

type Props = {
  src: string;
  poster?: string | null;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
};

export const LazyVideo = ({
  src,
  poster,
  className,
  autoPlay = true,
  loop = true,
  muted = true,
  controls = false,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const { isMobile, prefersSaveData, isSlowConnection } = useDeviceProfile();
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [hasPlayError, setHasPlayError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const effectiveAutoPlay = useMemo(
    () => autoPlay && !prefersReducedMotion && !prefersSaveData && !isSlowConnection && !isMobile,
    [autoPlay, prefersReducedMotion, prefersSaveData, isSlowConnection, isMobile],
  );
  const requiresUserAction = !effectiveAutoPlay || hasPlayError;
  const showOverlay = shouldLoad && requiresUserAction && !hasPlayed;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let observer: IntersectionObserver | null = null;
    const onIntersect: IntersectionObserverCallback = (entries) => {
      const entry = entries[0];
      if (entry && entry.isIntersecting) {
        setShouldLoad(true);
      }
    };
    observer = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: '200px 0px',
      threshold: 0.1,
    });
    observer.observe(el);
    return () => {
      observer?.disconnect();
    };
  }, []);

  const playVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
      setHasPlayed(true);
      setHasPlayError(false);
    } catch (error) {
      setHasPlayError(true);
    }
  }, []);

  const handleManualPlay = useCallback(() => {
    setShouldLoad(true);
    void playVideo();
  }, [playVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;

    const handleVisibility = () => {
      const rect = video.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;
      if (!isInView) {
        video.pause();
      } else if (effectiveAutoPlay) {
        void playVideo();
      }
    };

    window.addEventListener('scroll', handleVisibility, { passive: true });
    window.addEventListener('resize', handleVisibility);
    handleVisibility();

    return () => {
      window.removeEventListener('scroll', handleVisibility);
      window.removeEventListener('resize', handleVisibility);
    };
  }, [effectiveAutoPlay, shouldLoad, playVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;

    const handleCanPlay = () => {
      setIsLoaded(true);
      if (effectiveAutoPlay) {
        void playVideo();
      }
    };

    const handleLoadedData = () => setIsLoaded(true);

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadeddata', handleLoadedData);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [effectiveAutoPlay, playVideo, shouldLoad]);

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <video
        ref={videoRef}
        className={className}
        playsInline
        crossOrigin="anonymous"
        muted={muted}
        loop={loop}
        preload={shouldLoad ? 'auto' : 'metadata'}
        poster={poster ?? undefined}
        controls={controls || requiresUserAction}
        // only set src when needed to avoid early network usage
        {...(shouldLoad ? { src } : {})}
      />
      {!isLoaded && shouldLoad && (
        <div className="pointer-events-none absolute inset-0 bg-black/35" aria-hidden="true">
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/40 border-t-white/90" />
          </div>
        </div>
      )}
      {showOverlay && (
        <button
          type="button"
          className="absolute inset-0 flex w-full items-center justify-center bg-black/40 px-4 text-center text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-black/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40"
          onClick={handleManualPlay}
          aria-label="Avvia video"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold tracking-[0.35em]">
            Tap to play
          </span>
        </button>
      )}
    </div>
  );
};
